import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { ACTOR_ID_HEADER_KEY, API_BASE_URL } from "constants";
import apiClient from "utils/apiClient";


const BASE_URL = `${API_BASE_URL}/api/v1/location-restrictions`;

const getActorHeaders = () => {
  const actorId =
    sessionStorage.getItem("actorId") || sessionStorage.getItem("userId");
  return actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {};
};

const getApiMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  return data.message || data?.data?.message || fallback;
};

const extractItems = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.content)) return data.content;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.data?.content)) return data.data.content;
  return [];
};

const extractTotals = (data, fallbackCount = 0) => {
  const safe = data?.data ?? data;
  return {
    totalPages: Number(safe?.totalPages ?? 1) || 1,
    totalElements: Number(safe?.totalElements ?? fallbackCount) || fallbackCount,
  };
};

const toOliveebAppEnumName = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (!trimmed.includes("-")) return trimmed;
  if (trimmed === "oliveeb-postman-internal") return "OLIVEEB_POSTMAN";
  return trimmed.replaceAll("-", "_").toUpperCase();
};

const toOliveebAppWireValue = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (trimmed.includes("-")) return trimmed.toLowerCase();

  // enum name -> hyphenated string
  if (trimmed.toUpperCase() === "OLIVEEB_POSTMAN") return "oliveeb-postman-internal";
  return trimmed.replaceAll("_", "-").toLowerCase();
};

export default function useLocationRestrictions(page, pageSize, applicationId) {
  const authToken = sessionStorage.getItem("authToken");
  const [restrictions, setRestrictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const handleAxiosErrors = (err, action = "operation") => {
    const message =
      err?.message ||
      err?.response?.data?.message ||
      `Failed to ${action}. Please try again.`;
    toast.error(message, { theme: "colored" });
    console.error(`${action} error:`, err);
  };

  const fetchAll = useCallback(
    async (fetchPage = page, fetchSize = pageSize) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (applicationId) {
          // Backend binds `app` as an enum via Spring -> must be enum name.
          params.set("app", toOliveebAppEnumName(applicationId));
        }
        params.set("page", String(fetchPage));
        params.set("size", String(fetchSize));
        const res = await apiClient.get(
          `${BASE_URL}?${params.toString()}`,
        );
        const items = extractItems(res.data);
        setRestrictions(items);
        const totals = extractTotals(res.data, items.length);
        setTotalPages(totals.totalPages);
        setTotalElements(totals.totalElements);
        return items;
      } catch (err) {
        handleAxiosErrors(err, "fetch location restrictions");
      } finally {
        setLoading(false);
      }
    },
    [authToken, page, pageSize, applicationId],
  );

  const getById = useCallback(
    async (id) => {
      if (!authToken || !id) return null;
      setLoading(true);
      try {
        const res = await apiClient.get(`${BASE_URL}/${id}`);
        return res.data ?? null;
      } catch (err) {
        handleAxiosErrors(err, "fetch location restriction");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  const create = useCallback(
    async (payload) => {
      if (!authToken) return null;
      const app = payload?.oliveebApp || payload?.app || applicationId;
      if (!app) {
        toast.error("App Name is required.", { theme: "colored" });
        return null;
      }
      try {
        const res = await apiClient.post(
          BASE_URL,
          // Backend deserializes OliveebApp using @JsonValue (hyphenated strings), so send the hyphenated wire value.
          { ...payload, oliveebApp: toOliveebAppWireValue(app) },
          {
          headers: getActorHeaders(),
          },
        );
        const created = res.data ?? null;
        if (created) {
          setRestrictions((prev) => [created, ...prev]);
          setTotalElements((prev) => prev + 1);
        }
        toast.success(getApiMessage(res.data, "Location restriction created"), {
          theme: "colored",
        });
        return created;
      } catch (err) {
        handleAxiosErrors(err, "create location restriction");
        throw err;
      }
    },
    [authToken, applicationId],
  );

  const update = useCallback(
    async (id, payload) => {
      if (!authToken) return null;
      try {
        const res = await apiClient.put(
          `${BASE_URL}/${id}`,
          {
            ...payload,
            ...(payload?.oliveebApp ? { oliveebApp: toOliveebAppWireValue(payload.oliveebApp) } : {}),
          },
          {
          headers: getActorHeaders(),
          },
        );
        const updated = res.data ?? null;
        if (updated) {
          setRestrictions((prev) =>
            prev.map((r) => (r.id === id ? updated : r)),
          );
        }
        toast.success(getApiMessage(res.data, "Location restriction updated"), {
          theme: "colored",
        });
        return updated;
      } catch (err) {
        handleAxiosErrors(err, "update location restriction");
        throw err;
      }
    },
    [authToken],
  );

  const updateActive = useCallback(
    async (id, active) => {
      if (!authToken || !id) return null;
      setLoading(true);
      try {
        const res = await apiClient.patch(
          `${BASE_URL}/${id}/active`,
          { active: Boolean(active) },
          { headers: getActorHeaders() },
        );
        const updated = res.data ?? null;
        if (updated) {
          setRestrictions((prev) =>
            prev.map((r) => (r.id === id ? updated : r)),
          );
        }
        toast.success(getApiMessage(res.data, "Active status updated"), {
          theme: "colored",
        });
        return updated;
      } catch (err) {
        handleAxiosErrors(err, "update active status");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  const updateAccessType = useCallback(
    async (id, accessType) => {
      if (!authToken || !id) return null;
      setLoading(true);
      try {
        const res = await apiClient.patch(
          `${BASE_URL}/${id}/access`,
          { accessType },
          { headers: getActorHeaders() },
        );
        const updated = res.data ?? null;
        if (updated) {
          setRestrictions((prev) =>
            prev.map((r) => (r.id === id ? updated : r)),
          );
        }
        toast.success(getApiMessage(res.data, "Access type updated"), {
          theme: "colored",
        });
        return updated;
      } catch (err) {
        handleAxiosErrors(err, "update access type");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  const checkAccess = useCallback(
    async ({ oliveebApp, country, state, county, city }) => {
      if (!authToken || !oliveebApp || !country) return null;
      try {
        const params = new URLSearchParams({
          // Backend binds `app` to an enum via Spring -> must be enum name.
          app: toOliveebAppEnumName(oliveebApp),
          country: String(country),
        });
        if (state) params.append("state", String(state));
        if (county) params.append("county", String(county));
        if (city) params.append("city", String(city));
        const res = await apiClient.get(`${BASE_URL}/check?${params.toString()}`);
        return res.data ?? null;
      } catch (err) {
        handleAxiosErrors(err, "check access");
        return null;
      }
    },
    [authToken],
  );

  const remove = useCallback(
    async (id) => {
      if (!authToken) return;
      try {
        const res = await apiClient.delete(`${BASE_URL}/${id}`);
        setRestrictions((prev) => prev.filter((r) => r.id !== id));
        setTotalElements((prev) => Math.max(0, prev - 1));
        toast.success(getApiMessage(res.data, "Location restriction deleted"), {
          theme: "colored",
        });
      } catch (err) {
        handleAxiosErrors(err, "delete location restriction");
        throw err;
      }
    },
    [authToken],
  );

  return {
    restrictions,
    loading,
    totalPages,
    totalElements,
    fetchAll,
    getById,
    checkAccess,
    create,
    update,
    updateActive,
    updateAccessType,
    remove,
  };
}
